/**
 * Widget analytics session id.
 *
 * One id per page load: generated lazily on first use, held in memory, and
 * reused for the whole session (including client-side route navigation within
 * the widget). A hard reload starts a fresh module scope and therefore a new
 * id — intentionally no persistence and no inactivity timeout.
 *
 * This is the widget counterpart to the member app's `sessionManager.ts`, but
 * deliberately simpler: the member app additionally resets the id after 30
 * minutes of inactivity, whereas the widget ties a session strictly to a page
 * load. Persisting the id (sessionStorage/localStorage) would survive a reload
 * and break "reload → new session", so we keep it in memory only.
 */

let currentSessionId: string | null = null;

/**
 * Generate a v4 UUID without ever throwing.
 *
 * `crypto.randomUUID()` is only defined in a secure context — the widget is
 * embedded on third-party restaurant sites and webviews, some of which are
 * served over plain HTTP, so it can be missing. A throw here would be swallowed
 * by `trackSegment`'s catch and drop the whole event, so we fall back to
 * `crypto.getRandomValues()` (available in insecure contexts too) and assemble
 * a standard v4 id ourselves.
 *
 * Shared with the embed relay's per-open `anonymousId` (see `segment.ts`), which
 * needs the same insecure-context-safe generator on partner pages.
 */
export function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
}

/**
 * The current widget analytics session id. Stable for the lifetime of the page
 * (and across in-app navigation); a new value after every hard reload.
 */
export function getSessionId(): string {
  if (!currentSessionId) {
    currentSessionId = generateUuid();
  }
  return currentSessionId;
}

/**
 * Drop the current session id so the next `getSessionId()` mints a fresh one.
 *
 * Called from the analytics `reset()` path (e.g. re-verification on a shared
 * browser): without this, only a hard reload regenerates the id, so the next
 * person's events would group under the prior member's widget session even
 * though Segment has already started a fresh anonymous identity.
 */
export function resetSessionId(): void {
  currentSessionId = null;
}
